import React, { useState } from 'react';

const MetricCard = ({ label, value, sub, subColor = 'text-gray-400', icon, iconBg }) => (
  <div className="stat-card rounded-2xl p-5 flex flex-col gap-2 hover:scale-[1.02] transition-transform duration-200">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center text-sm`}>
        <i className={`fas ${icon}`}></i>
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-800 tabular-nums">{value}</p>
    <p className={`text-xs font-medium ${subColor}`}>{sub}</p>
  </div>
);

const AlertRow = ({ icon, iconColor, bg, border, title, body }) => (
  <div className={`flex gap-3 p-4 rounded-xl border ${bg} ${border} msg-enter`}>
    <i className={`fas ${icon} ${iconColor} mt-0.5 flex-shrink-0 text-base`}></i>
    <div className="text-sm text-gray-700">
      <span className="font-semibold">{title}</span> {body}
    </div>
  </div>
);

export default function SalesPanel() {
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSimulate = () => {
    setLoading(true);
    setShowResult(false);
    setTimeout(() => {
      setLoading(false);
      setShowResult(true);
    }, 1200);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-violet-600 flex items-center justify-center text-white shadow-md">
          <i className="fas fa-handshake"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Sales &amp; Credit Control</h2>
          <p className="text-xs text-gray-400">Pre-commitment validation · credit limits · stock availability</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        <MetricCard label="Orders needing approval" value="5"  sub="$211k value"     subColor="text-amber-500"   icon="fa-file-signature"  iconBg="bg-amber-100 text-amber-600" />
        <MetricCard label="Credit holds"            value="3"  sub="limit exceeded"  subColor="text-red-500"     icon="fa-lock"             iconBg="bg-red-100 text-red-600" />
        <MetricCard label="Stock conflicts"         value="2"  sub="shortage"        subColor="text-blue-500"    icon="fa-boxes-stacked"    iconBg="bg-blue-100 text-blue-600" />
      </div>

      {/* Simulation engine */}
      <div className="stat-card rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center text-sm">
            <i className="fas fa-flask"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Order Validation Engine</h3>
            <p className="text-xs text-gray-400">Agentic credit-limit + stock-availability check on a draft order</p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Draft order</p>
            <p className="text-sm font-semibold text-gray-800">C10025 · $67,000 · 3 line items</p>
          </div>
          <button
            onClick={handleSimulate}
            disabled={loading}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow hover:shadow-lg hover:scale-105 active:scale-100 disabled:opacity-60 disabled:scale-100 transition-all duration-200"
          >
            {loading
              ? <><i className="fas fa-circle-notch fa-spin text-xs"></i> Validating…</>
              : <><i className="fas fa-play text-xs"></i> Run validation</>
            }
          </button>
        </div>

        {showResult && (
          <div className="space-y-3">
            <AlertRow
              icon="fa-circle-xmark" iconColor="text-red-500"
              bg="bg-red-50" border="border-red-100"
              title="Credit limit exceeded."
              body="C10025 limit $50,000 · balance $28,000 · this order $67,000 → exceeds available credit by $45,000."
            />
            <AlertRow
              icon="fa-triangle-exclamation" iconColor="text-amber-500"
              bg="bg-amber-50" border="border-amber-100"
              title="Stock shortage."
              body="ITM1105 short by 88 units (on-hand 12, requested 100). Earliest replenishment in 9 days."
            />
            <AlertRow
              icon="fa-lightbulb" iconColor="text-blue-500"
              bg="bg-blue-50" border="border-blue-100"
              title="Recommended action:"
              body="Approve a partial shipment of available stock, require 30% prepayment ($20,100) to bring exposure within limit, and route residual to manager override with a payment plan."
            />
          </div>
        )}
      </div>
    </div>
  );
}
